import React, { useEffect, useState } from 'react';
import { Settings as SettingsIcon, Clock, Plus, Trash2, Loader2, Globe, Target } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useWarehouse } from '../context/WarehouseContext';
import { useI18n } from '../i18n/I18nContext';
import { PageHeader } from '../components/common';
import NotificationPanel, { Toggle } from '../components/NotificationPanel';
import { TIMEZONES, COUNTRIES, setPrefsCache } from '../utils/prefs';
import api from '../api/client';

export default function Settings() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [alerts, setAlerts] = useState(true);
  const [dark, setDark] = useState(true);
  const [interval, setInterval] = useState('30');
  const [shifts, setShifts] = useState([]);
  const [loadingShifts, setLoadingShifts] = useState(true);
  const [savingShifts, setSavingShifts] = useState(false);
  const [autoReorder, setAutoReorder] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [timezone, setTimezone] = useState('');
  const [country, setCountry] = useState('');
  const [currency, setCurrency] = useState('₹');
  const [costPerHour, setCostPerHour] = useState(0);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [curPw, setCurPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [changingPw, setChangingPw] = useState(false);

  const changePassword = async () => {
    if (newPw.length < 6) { toast.error(t('set.pwShort')); return; }
    if (newPw !== confirmPw) { toast.error(t('set.pwMismatch')); return; }
    setChangingPw(true);
    try {
      await api.changePassword(curPw, newPw);
      toast.success(t('set.pwUpdated'));
      setCurPw(''); setNewPw(''); setConfirmPw('');
    } catch (e) {
      toast.error(t('set.pwFailed'), { description: e?.response?.data?.detail || t('set.tryAgain') });
    } finally { setChangingPw(false); }
  };

  const resetAll = async () => {
    if (!window.confirm(t('set.resetConfirm1'))) return;
    if (!window.confirm(t('set.resetConfirm2'))) return;
    setResetting(true);
    try {
      const r = await api.resetAppData();
      const total = Object.values(r.deleted || {}).reduce((s, v) => s + v, 0);
      toast.success(t('set.resetDone'), { description: t('set.resetDoneDesc').replace('{n}', total) });
    } catch (e) {
      toast.error(t('set.resetFailed'), { description: e?.response?.data?.detail || t('set.adminRequired') });
    } finally { setResetting(false); }
  };

  useEffect(() => {
    api.getShifts().then(setShifts).catch(() => {}).finally(() => setLoadingShifts(false));
    api.getAutoReorder().then((r) => setAutoReorder(!!r.enabled)).catch(() => {});
    api.getPreferences().then((p) => { setTimezone(p.timezone || ''); setCountry(p.country || ''); setCurrency(p.currency || '₹'); setCostPerHour(p.downtimeCostPerHour || 0); setPrefsCache(p); }).catch(() => {});
  }, []);

  const savePrefs = async () => {
    setSavingPrefs(true);
    try {
      const saved = await api.setPreferences({ timezone, country, currency, downtimeCostPerHour: costPerHour });
      setPrefsCache(saved);
      toast.success(t('set.regionalSaved'), { description: t('set.regionalSavedDesc') });
    } catch (e) { toast.error(t('set.regionalFailed')); }
    finally { setSavingPrefs(false); }
  };

  const toggleAutoReorder = async () => {
    const next = !autoReorder;
    setAutoReorder(next);
    try { await api.setAutoReorder(next); toast.success(next ? t('set.autoReorderOn') : t('set.autoReorderOff')); }
    catch (e) { toast.error(t('set.failed')); setAutoReorder(!next); }
  };

  const save = () => toast.success(t('set.settingsSaved'));

  const updateShift = (idx, field, value) => {
    setShifts((p) => p.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  };
  const addShift = () => setShifts((p) => [...p, { id: '', name: 'New Shift', start: '00:00', end: '08:00', breakStart: '', breakEnd: '' }]);
  const removeShift = (idx) => setShifts((p) => p.filter((_, i) => i !== idx));

  const saveShifts = async () => {
    setSavingShifts(true);
    try {
      const saved = await api.saveShifts(shifts.map(({ id, name, start, end, breakStart, breakEnd }) => ({ id, name, start, end, breakStart, breakEnd })));
      setShifts(saved);
      toast.success(t('set.shiftsSaved'));
    } catch (e) { toast.error(t('set.shiftsFailed')); }
    finally { setSavingShifts(false); }
  };

  return (
    <div className="kmt-fade-in">
      <PageHeader icon={SettingsIcon} title={t('set.title')} subtitle={t('set.subtitle')} />
      <div className="p-8 max-w-3xl space-y-6">
        <div className="kmt-panel p-6">
          <div className="font-display font-semibold mb-4">{t('set.profile')}</div>
          <div className="space-y-4">
            <div><label className="kmt-label">{t('col.name')}</label><input className="kmt-input mt-2" defaultValue={user?.name} /></div>
            <div><label className="kmt-label">{t('col.email')}</label><input className="kmt-input mt-2" defaultValue={user?.email} /></div>
            <div><label className="kmt-label">{t('col.role')}</label><input className="kmt-input mt-2" defaultValue={user?.role} disabled /></div>
          </div>
        </div>

        {/* ---- Regional (Timezone & Country) ---- */}
        <div className="kmt-panel p-6" data-testid="regional-panel">
          <div className="font-display font-semibold flex items-center gap-2 mb-1"><Globe size={17} style={{ color: 'var(--kmt-gold)' }} />{t('set.regional')}</div>
          <div className="text-xs mb-4" style={{ color: 'var(--kmt-muted)' }}>{t('set.regionalDesc')}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="kmt-label">{t('set.timezone')}</label>
              <select className="kmt-input mt-2" value={timezone} onChange={(e) => setTimezone(e.target.value)} data-testid="settings-timezone-select">
                <option value="">{t('set.browserDefault')} ({Intl.DateTimeFormat().resolvedOptions().timeZone})</option>
                {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </div>
            <div>
              <label className="kmt-label">{t('set.country')}</label>
              <select className="kmt-input mt-2" value={country} onChange={(e) => setCountry(e.target.value)} data-testid="settings-country-select">
                <option value="">{t('set.selectOpt')}</option>
                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="kmt-label">{t('set.currency')}</label>
              <input className="kmt-input mt-2" value={currency} maxLength={4} onChange={(e) => setCurrency(e.target.value)} placeholder="₹" data-testid="settings-currency-input" />
            </div>
            <div>
              <label className="kmt-label">{t('set.downtimeCost')}</label>
              <input className="kmt-input mt-2" type="number" min="0" value={costPerHour} onChange={(e) => setCostPerHour(Math.max(0, parseFloat(e.target.value || '0')))} placeholder="0" data-testid="settings-downtime-cost-input" />
              <div className="text-xs mt-1" style={{ color: 'var(--kmt-muted)' }}>{t('set.downtimeCostDesc')}</div>
            </div>
          </div>
          <button className="kmt-btn-gold mt-4 flex items-center gap-2" disabled={savingPrefs} onClick={savePrefs} data-testid="save-regional-btn">
            {savingPrefs ? <Loader2 className="animate-spin" size={15} /> : null}{t('set.saveRegional')}
          </button>
        </div>

        {user?.role === 'ADMIN' && <WarehousesPanel />}

        {(user?.role === 'ADMIN' || user?.role === 'SUPERVISOR') && <MaterialGoalsPanel />}

        {/* ---- Editable Shift Timings ---- */}
        <div className="kmt-panel p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display font-semibold flex items-center gap-2"><Clock size={17} style={{ color: 'var(--kmt-gold)' }} />{t('set.shiftTimings')}</div>
            <button className="kmt-btn-ghost text-xs flex items-center gap-1.5" onClick={addShift}><Plus size={14} />{t('set.addShift')}</button>
          </div>
          {loadingShifts ? (
            <div className="flex items-center justify-center py-8" style={{ color: 'var(--kmt-muted)' }}><Loader2 className="animate-spin" size={22} /></div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-12 gap-3 kmt-label" style={{ fontSize: 9 }}>
                <div className="col-span-3">{t('set.shiftName')}</div><div className="col-span-2">{t('set.start')}</div><div className="col-span-2">{t('set.end')}</div><div className="col-span-2">{t('set.breakStart')}</div><div className="col-span-2">{t('set.breakEnd')}</div><div className="col-span-1" />
              </div>
              {shifts.map((s, i) => (
                <div key={i} className="grid grid-cols-12 gap-3 items-center">
                  <input className="kmt-input col-span-3" value={s.name} onChange={(e) => updateShift(i, 'name', e.target.value)} data-testid={`shift-name-${i}`} />
                  <input className="kmt-input col-span-2" type="time" value={s.start} onChange={(e) => updateShift(i, 'start', e.target.value)} data-testid={`shift-start-${i}`} />
                  <input className="kmt-input col-span-2" type="time" value={s.end} onChange={(e) => updateShift(i, 'end', e.target.value)} data-testid={`shift-end-${i}`} />
                  <input className="kmt-input col-span-2" type="time" value={s.breakStart || ''} onChange={(e) => updateShift(i, 'breakStart', e.target.value)} data-testid={`shift-break-start-${i}`} />
                  <input className="kmt-input col-span-2" type="time" value={s.breakEnd || ''} onChange={(e) => updateShift(i, 'breakEnd', e.target.value)} data-testid={`shift-break-end-${i}`} />
                  <button className="col-span-1 flex justify-center" onClick={() => removeShift(i)} style={{ color: 'var(--kmt-muted)' }} data-testid={`shift-remove-${i}`}><Trash2 size={16} className="hover:text-[var(--kmt-red)]" /></button>
                </div>
              ))}
              <div className="kmt-label" style={{ fontSize: 10, color: 'var(--kmt-muted-2)' }}>{t('set.breakHint')}</div>
              <button className="kmt-btn-gold mt-2" disabled={savingShifts} onClick={saveShifts} data-testid="save-shifts-btn">{savingShifts ? t('set.saving') : t('set.saveShifts')}</button>
            </div>
          )}
        </div>

        <div className="kmt-panel p-6">
          <div className="font-display font-semibold mb-4">{t('set.shopfloor')}</div>
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <div><div className="text-sm font-medium">{t('set.autoRefresh')}</div><div className="text-xs" style={{ color: 'var(--kmt-muted)' }}>{t('set.autoRefreshDesc')}</div></div>
              <Toggle on={autoRefresh} onClick={() => setAutoRefresh(!autoRefresh)} />
            </div>
            <div className="flex items-center justify-between">
              <div><div className="text-sm font-medium">{t('set.refreshInterval')}</div><div className="text-xs" style={{ color: 'var(--kmt-muted)' }}>{t('set.refreshIntervalDesc')}</div></div>
              <select className="kmt-input w-28" value={interval} onChange={(e) => setInterval(e.target.value)}>
                <option value="15">15s</option><option value="30">30s</option><option value="60">60s</option>
              </select>
            </div>
            <div className="flex items-center justify-between">
              <div><div className="text-sm font-medium">{t('set.problemAlerts')}</div><div className="text-xs" style={{ color: 'var(--kmt-muted)' }}>{t('set.problemAlertsDesc')}</div></div>
              <Toggle on={alerts} onClick={() => setAlerts(!alerts)} />
            </div>
            <div className="flex items-center justify-between">
              <div><div className="text-sm font-medium">{t('set.autoReorder')}</div><div className="text-xs" style={{ color: 'var(--kmt-muted)' }}>{t('set.autoReorderDesc')}</div></div>
              <Toggle on={autoReorder} onClick={toggleAutoReorder} />
            </div>
            <NotificationPanel />
            <div className="flex items-center justify-between">
              <div><div className="text-sm font-medium">{t('set.darkTheme')}</div><div className="text-xs" style={{ color: 'var(--kmt-muted)' }}>{t('set.darkThemeDesc')}</div></div>
              <Toggle on={dark} onClick={() => setDark(!dark)} />
            </div>
          </div>
        </div>

        <button className="kmt-btn-gold" onClick={save}>{t('set.saveChanges')}</button>

        <div className="kmt-panel p-6" data-testid="change-password-panel">
          <div className="font-display font-semibold mb-1">{t('set.changePw')}</div>
          <div className="text-xs mb-4" style={{ color: 'var(--kmt-muted)' }}>{t('set.changePwDesc')}</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input type="password" className="kmt-input" placeholder={t('set.currentPw')} value={curPw} onChange={(e) => setCurPw(e.target.value)} data-testid="current-password-input" />
            <input type="password" className="kmt-input" placeholder={t('set.newPw')} value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="new-password-input" />
            <input type="password" className="kmt-input" placeholder={t('set.confirmPw')} value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} data-testid="confirm-password-input" />
          </div>
          <button className="kmt-btn-gold mt-4 flex items-center gap-2" disabled={changingPw} onClick={changePassword} data-testid="change-password-btn">
            {changingPw ? <Loader2 className="animate-spin" size={15} /> : null}{t('set.updatePw')}
          </button>
        </div>

        {user?.role === 'ADMIN' && (
          <div className="kmt-panel p-6" style={{ border: '1px solid rgba(239,68,68,0.4)' }} data-testid="danger-zone">
            <div className="font-display font-semibold mb-1" style={{ color: 'var(--kmt-red)' }}>{t('set.danger')}</div>
            <div className="text-xs mb-4" style={{ color: 'var(--kmt-muted)' }}>{t('set.dangerDesc')}</div>
            <button className="kmt-btn-ghost flex items-center gap-2" style={{ color: 'var(--kmt-red)', borderColor: 'var(--kmt-red)' }}
              disabled={resetting} onClick={resetAll} data-testid="reset-app-data-btn">
              {resetting ? <Loader2 className="animate-spin" size={15} /> : <Trash2 size={15} />}{t('set.resetAll')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


function WarehousesPanel() {
  const { t } = useI18n();
  const wh = useWarehouse();
  const [list, setList] = useState([]);
  const [name, setName] = useState('');
  const [loc, setLoc] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = () => { api.listWarehouses().then(setList).catch(() => {}); wh?.refresh?.(); };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try { await api.createWarehouse({ name, location: loc }); setName(''); setLoc(''); reload(); toast.success(t('set.factoryAdded')); }
    catch (e) { toast.error(e?.response?.data?.detail || t('set.failedAdd')); }
    finally { setBusy(false); }
  };
  const rename = async (w, patch) => {
    try { await api.updateWarehouse(w.id, patch); reload(); toast.success(t('set.factoryUpdated')); }
    catch (e) { toast.error(t('set.updateFailed')); }
  };
  const remove = async (w) => {
    if (!window.confirm(t('set.factoryDeleteConfirm').replace('{name}', w.name))) return;
    try { await api.deleteWarehouse(w.id); reload(); toast.success(t('set.factoryDeleted')); }
    catch (e) { toast.error(e?.response?.data?.detail || t('set.deleteFailed')); }
  };

  return (
    <div className="kmt-panel p-6" data-testid="warehouses-panel">
      <div className="font-display font-semibold flex items-center gap-2 mb-1"><Globe size={17} style={{ color: 'var(--kmt-gold)' }} />{t('set.factories')}</div>
      <div className="text-xs mb-4" style={{ color: 'var(--kmt-muted)' }}>{t('set.factoriesDesc')}</div>
      <div className="space-y-2 mb-4">
        {list.map((w) => (
          <div key={w.id} className="flex items-center gap-2 rounded-lg px-3 py-2" style={{ background: 'var(--kmt-panel-2)', border: '1px solid var(--kmt-border)' }} data-testid={`wh-row-${w.id}`}>
            <input className="kmt-input flex-1" defaultValue={w.name} onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== w.name) rename(w, { name: v }); }} data-testid={`wh-name-${w.id}`} />
            <input className="kmt-input flex-1" defaultValue={w.location || ''} placeholder={t('set.location')} onBlur={(e) => { const v = e.target.value.trim(); if (v !== (w.location || '')) rename(w, { location: v }); }} data-testid={`wh-location-${w.id}`} />
            <span className="font-mono-k text-xs" style={{ color: 'var(--kmt-gold)' }}>{w.id}</span>
            <button className="text-xs px-2 py-1 rounded" style={{ color: 'var(--kmt-red)' }} onClick={() => remove(w)} data-testid={`wh-delete-${w.id}`}><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <input className="kmt-input flex-1" placeholder={t('set.newFactoryName')} value={name} onChange={(e) => setName(e.target.value)} data-testid="wh-new-name" />
        <input className="kmt-input flex-1" placeholder={t('set.locationOpt')} value={loc} onChange={(e) => setLoc(e.target.value)} data-testid="wh-new-location" />
        <button className="kmt-btn-gold flex items-center gap-2" disabled={busy} onClick={add} data-testid="wh-add-btn"><Plus size={15} />{t('set.add')}</button>
      </div>
    </div>
  );
}

function MaterialGoalsPanel() {
  const { t } = useI18n();
  const [goal, setGoal] = useState('');
  const [mult, setMult] = useState('1.5');
  const [over, setOver] = useState('5');
  const [loading, setLoading] = useState(true);
  const [savingGoal, setSavingGoal] = useState(false);
  const [savingCfg, setSavingCfg] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [newReason, setNewReason] = useState('');
  const [addingReason, setAddingReason] = useState(false);

  const loadReasons = () => api.listSettingReasons().then(setReasons).catch(() => {});

  useEffect(() => {
    Promise.all([
      api.getChangeoverTarget().then((r) => setGoal(r.target ? String(r.target) : '')).catch(() => {}),
      api.getMaterialSettingConfig().then((c) => { setMult(String(c.slowMultiplier ?? 1.5)); setOver(String(c.slowMinOver ?? 5)); }).catch(() => {}),
      loadReasons(),
    ]).finally(() => setLoading(false));
  }, []);

  const addReason = async () => {
    const name = newReason.trim();
    if (!name) return;
    setAddingReason(true);
    try { await api.createSettingReason(name); setNewReason(''); loadReasons(); toast.success(t('set.reasonAdded')); }
    catch (e) { toast.error(e?.response?.data?.detail || t('set.reasonAddFailed')); }
    finally { setAddingReason(false); }
  };
  const removeReason = async (r) => {
    try { await api.deleteSettingReason(r.id); loadReasons(); toast.success(t('set.reasonRemoved')); }
    catch (e) { toast.error(t('set.deleteFailed')); }
  };

  const saveGoal = async () => {
    const n = Math.max(0, parseInt(goal || '0', 10));
    setSavingGoal(true);
    try {
      await api.setChangeoverTarget(n);
      toast.success(n ? t('set.goalSet').replace('{n}', n) : t('set.goalCleared'));
    } catch (e) { toast.error(e?.response?.data?.detail || t('set.goalFailed')); }
    finally { setSavingGoal(false); }
  };

  const saveCfg = async () => {
    const m = Math.max(1, Math.min(5, parseFloat(mult || '1.5')));
    const o = Math.max(0, Math.min(120, parseInt(over || '5', 10)));
    setSavingCfg(true);
    try {
      const saved = await api.setMaterialSettingConfig({ slowMultiplier: m, slowMinOver: o });
      setMult(String(saved.slowMultiplier)); setOver(String(saved.slowMinOver));
      toast.success(t('set.rulesSaved'), { description: t('set.rulesSavedDesc') });
    } catch (e) { toast.error(e?.response?.data?.detail || t('set.rulesFailed')); }
    finally { setSavingCfg(false); }
  };

  return (
    <div className="kmt-panel p-6" data-testid="material-goals-panel">
      <div className="font-display font-semibold flex items-center gap-2 mb-1"><Target size={17} style={{ color: 'var(--kmt-gold)' }} />{t('set.matGoals')}</div>
      <div className="text-xs mb-4" style={{ color: 'var(--kmt-muted)' }}>{t('set.matGoalsDesc')}</div>
      {loading ? (
        <div className="flex items-center justify-center py-8" style={{ color: 'var(--kmt-muted)' }}><Loader2 className="animate-spin" size={22} /></div>
      ) : (
        <div className="space-y-5">
          <div>
            <label className="kmt-label">{t('set.teamGoal')}</label>
            <div className="text-xs mb-2" style={{ color: 'var(--kmt-muted)' }}>{t('set.teamGoalDesc')}</div>
            <div className="flex gap-2">
              <input className="kmt-input w-40" type="number" min="0" value={goal} onChange={(e) => setGoal(e.target.value)} placeholder={t('set.goalPlaceholder')} data-testid="settings-goal-input" />
              <button className="kmt-btn-gold flex items-center gap-2" disabled={savingGoal} onClick={saveGoal} data-testid="settings-save-goal-btn">
                {savingGoal ? <Loader2 className="animate-spin" size={15} /> : null}{t('set.saveGoal')}
              </button>
            </div>
          </div>
          <div className="pt-4" style={{ borderTop: '1px solid var(--kmt-border)' }}>
            <label className="kmt-label">{t('set.slowFlag')}</label>
            <div className="text-xs mb-3" style={{ color: 'var(--kmt-muted)' }}>{t('set.slowFlagDesc')}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="kmt-label" style={{ fontSize: 9 }}>{t('set.multiplier')}</label>
                <input className="kmt-input mt-2 w-full" type="number" min="1" max="5" step="0.1" value={mult} onChange={(e) => setMult(e.target.value)} data-testid="settings-slow-mult-input" />
              </div>
              <div>
                <label className="kmt-label" style={{ fontSize: 9 }}>{t('set.minOver')}</label>
                <input className="kmt-input mt-2 w-full" type="number" min="0" max="120" value={over} onChange={(e) => setOver(e.target.value)} data-testid="settings-slow-over-input" />
              </div>
            </div>
            <button className="kmt-btn-gold mt-4 flex items-center gap-2" disabled={savingCfg} onClick={saveCfg} data-testid="settings-save-material-cfg-btn">
              {savingCfg ? <Loader2 className="animate-spin" size={15} /> : null}{t('set.saveRules')}
            </button>
          </div>
          <div className="pt-4" style={{ borderTop: '1px solid var(--kmt-border)' }}>
            <label className="kmt-label">{t('set.reasonPresets')}</label>
            <div className="text-xs mb-3" style={{ color: 'var(--kmt-muted)' }}>{t('set.reasonPresetsDesc')}</div>
            <div className="space-y-2 mb-3">
              {reasons.map((r) => (
                <div key={r.id} className="flex items-center gap-2 rounded-lg px-3 py-2" style={{ background: 'var(--kmt-panel-2)', border: '1px solid var(--kmt-border)' }} data-testid={`setting-reason-row-${r.id}`}>
                  <span className="flex-1 text-sm">{r.name}</span>
                  <button className="text-xs" style={{ color: 'var(--kmt-red)' }} onClick={() => removeReason(r)} data-testid={`setting-reason-delete-${r.id}`}><Trash2 size={15} /></button>
                </div>
              ))}
              {reasons.length === 0 && <div className="text-xs" style={{ color: 'var(--kmt-muted)' }}>{t('set.noReasons')}</div>}
            </div>
            <div className="flex gap-2">
              <input className="kmt-input flex-1" placeholder={t('set.reasonPlaceholder')} value={newReason} onChange={(e) => setNewReason(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addReason(); }} data-testid="setting-reason-new-input" />
              <button className="kmt-btn-gold flex items-center gap-2" disabled={addingReason} onClick={addReason} data-testid="setting-reason-add-btn"><Plus size={15} />{t('set.add')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
